import { getMatchTier } from './substringMatch';

type SubstringSearchValue = string | readonly string[] | null | undefined;

type SearchValueGetter<T> = (item: T) => SubstringSearchValue;

type SearchableKey<T> = Extract<
  { [Key in keyof T]-?: T[Key] extends SubstringSearchValue ? Key : never }[keyof T],
  string
>;

const DEFAULT_KEY_PRIORITY = 1;

type MatchRank = {
  priority: number;
  tier: number;
  matchIndex: number;
};

/** Orders two matches, best first. Priority wins outright, then match quality, then match position. */
function compareRank(a: MatchRank, b: MatchRank): number {
  return b.priority - a.priority || a.tier - b.tier || a.matchIndex - b.matchIndex;
}

function matchValue(value: string, search: string, priority: number): MatchRank | undefined {
  const normalizedValue = value.toLowerCase();
  const matchIndex = normalizedValue.indexOf(search);
  if (matchIndex < 0) {
    return undefined;
  }

  return { priority, tier: getMatchTier(normalizedValue, search, matchIndex), matchIndex };
}

/** A field can hold one string or a list of them; its best-matching entry represents the whole field. */
function matchField(searchValue: string | readonly string[], search: string, priority: number): MatchRank | undefined {
  if (typeof searchValue === 'string') {
    return matchValue(searchValue, search, priority);
  }

  let best: MatchRank | undefined;
  for (const value of searchValue) {
    const candidate = matchValue(value, search, priority);
    if (candidate != null && (best == null || compareRank(candidate, best) < 0)) {
      best = candidate;
    }
  }
  return best;
}

function fieldIncludes(searchValue: string | readonly string[], search: string): boolean {
  return typeof searchValue === 'string'
    ? searchValue.toLowerCase().includes(search)
    : searchValue.some(value => value.toLowerCase().includes(search));
}

export function getSearchTerms(searchText: string, minMatchCharLength = 1): string[] {
  const terms = searchText
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(term => term.length >= minMatchCharLength);
  return [...new Set(terms)];
}

/**
 * Filters `items` to those matching `searchText`, preserving their input order.
 *
 * Prefer this over `findSubstringMatches` when the caller's existing order is meaningful — a user's
 * arrangement of cards, for example — because ranking would resequence the list. An empty search
 * matches everything, since an absent filter is not a constraint.
 * Fields can be property keys or getters for derived search values.
 */
export function filterBySubstring<T, Key extends SearchableKey<T>>(
  items: readonly T[],
  searchText: string,
  fields: readonly (Key | { getValue: SearchValueGetter<T> })[]
): T[] {
  const search = searchText.trim().toLowerCase();
  if (search.length === 0) {
    return [...items];
  }

  return items.filter(item =>
    fields.some(field => {
      const searchValue = typeof field === 'string' ? (item[field] as SubstringSearchValue) : field.getValue(item);
      return searchValue != null && fieldIncludes(searchValue, search);
    })
  );
}

/**
 * Performs deterministic case-insensitive substring search across fields, returning the matching
 * items in rank order.
 *
 * Results are ranked by field priority, then by exact/starts-with/ends-with/contains match quality,
 * then by match position, and finally retain their input order for ties. Priority dominates: a match
 * on a higher-priority field always outranks any match on a lower-priority one, however good.
 *
 * Highlight ranges are deliberately not returned. Only the rows a caller actually renders need
 * them, so callers compute them per row with `getSubstringMatchIndices`.
 *
 * A query below `minMatchCharLength` matches nothing.
 * Fields can be property keys, ranked property keys, or ranked getters for derived search values.
 */
export function findSubstringMatches<T, Key extends SearchableKey<T>>(
  items: readonly T[],
  searchText: string,
  fields: readonly (Key | { key: Key; priority: number } | { getValue: SearchValueGetter<T>; priority: number })[],
  { minMatchCharLength = 1 }: { minMatchCharLength?: number } = {}
): T[] {
  const search = searchText.trim().toLowerCase();
  if (search.length === 0 || search.length < minMatchCharLength) {
    return [];
  }

  const searchFields = fields.map(field =>
    typeof field === 'string' ? { key: field, priority: DEFAULT_KEY_PRIORITY } : field
  );

  const ranked: { item: T; rank: MatchRank }[] = [];

  for (const item of items) {
    let bestRank: MatchRank | undefined;

    for (const field of searchFields) {
      const searchValue = 'key' in field ? (item[field.key] as SubstringSearchValue) : field.getValue(item);
      if (searchValue == null) {
        continue;
      }

      const rank = matchField(searchValue, search, field.priority);
      if (rank != null && (bestRank == null || compareRank(rank, bestRank) < 0)) {
        bestRank = rank;
      }
    }

    if (bestRank != null) {
      ranked.push({ item, rank: bestRank });
    }
  }

  // Sorting is stable, so items sharing a rank keep their input order.
  return ranked.toSorted((a, b) => compareRank(a.rank, b.rank)).map(({ item }) => item);
}
